import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3200';

function authHeaders() {
  const token = JSON.parse(localStorage.getItem('urs-auth') ?? '{}')?.state?.token ?? '';
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

interface Employee {
  id: string;
  fullName: string;
  email: string;
  phone?: string;
  jobTitle?: string;
  department?: string;
  gender?: string;
  locationId?: string;
  employmentType: string;
  isActive: boolean;
  startDate: string;
  salaryStructures?: { basicSalaryUgx: number }[];
}

interface LeaveRequest {
  id: string;
  employee?: { fullName: string };
  leaveType: string;
  startDate: string;
  endDate: string;
  status: string;
}

interface Department {
  id: string;
  name: string;
  isActive: boolean;
}

interface JobTitle {
  id: string;
  name: string;
  isActive: boolean;
}

interface Location {
  id: string;
  name: string;
  address?: string;
  district?: string;
  phone?: string;
  isActive: boolean;
}

function Skeleton({ h = 56 }: { h?: number }) {
  return (
    <div style={{ height: h, borderRadius: 8, background: 'var(--surface2)', animation: 'pulse 1.5s infinite' }} />
  );
}

function fmtUGX(n: number) {
  return 'UGX ' + Math.round(n).toLocaleString('en-UG');
}

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString('en-UG', { day: 'numeric', month: 'short', year: 'numeric' });
}

function empTypeBadge(t: string) {
  if (t === 'fulltime') return 'badge-blue';
  if (t === 'parttime') return 'badge-green';
  if (t === 'contract') return 'badge-amber';
  return 'badge-gray';
}

function leaveStatusBadge(s: string) {
  if (s === 'PENDING') return 'badge-amber';
  if (s === 'APPROVED') return 'badge-green';
  if (s === 'REJECTED') return 'badge-red';
  return 'badge-gray';
}

function EmployeeModal({
  initial, departments, jobTitles, locations, onClose, onSave, isSaving,
}: {
  initial?: Partial<Employee>;
  departments: Department[];
  jobTitles: JobTitle[];
  locations: Location[];
  onClose: () => void;
  onSave: (data: object) => void;
  isSaving: boolean;
}) {
  const [fullName, setFullName] = useState(initial?.fullName ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [gender, setGender] = useState(initial?.gender ?? '');
  const [employmentType, setEmploymentType] = useState(initial?.employmentType ?? 'fulltime');
  const [baseSalary, setBaseSalary] = useState(initial?.salaryStructures?.[0]?.basicSalaryUgx ?? 0);
  const [startDate, setStartDate] = useState(initial?.startDate ? initial.startDate.slice(0, 10) : new Date().toISOString().slice(0, 10));
  const [department, setDepartment] = useState(initial?.department ?? '');
  const [jobTitle, setJobTitle] = useState(initial?.jobTitle ?? '');
  const [locationId, setLocationId] = useState(initial?.locationId ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2 className="modal-title">{initial?.id ? 'Edit Employee' : 'New Employee'}</h2>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>&times;</button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label className="field-label">Full Name *</label>
            <input className="input" value={fullName} onChange={e => setFullName(e.target.value)} placeholder="e.g. Amara Nakato" />
          </div>
          <div className="field">
            <label className="field-label">Email *</label>
            <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label">Phone</label>
            <input className="input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+256 7XX XXX XXX" />
          </div>
          <div className="field">
            <label className="field-label">Gender</label>
            <select className="input" value={gender} onChange={e => setGender(e.target.value)}>
              <option value="">— Select —</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="field">
            <label className="field-label">Employment Type</label>
            <select className="input" value={employmentType} onChange={e => setEmploymentType(e.target.value)}>
              <option value="fulltime">Full Time</option>
              <option value="parttime">Part Time</option>
              <option value="contract">Contract</option>
              <option value="casual">Casual</option>
            </select>
          </div>
          <div className="field">
            <label className="field-label">Start Date</label>
            <input className="input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label">Basic Salary (UGX)</label>
            <input className="input" type="number" value={baseSalary} onChange={e => setBaseSalary(parseInt(e.target.value) || 0)} />
          </div>
          <div className="field">
            <label className="field-label">Department *</label>
            <select className="input" value={department} onChange={e => setDepartment(e.target.value)}>
              <option value="">— Select department —</option>
              {departments.filter(d => d.isActive).map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="field-label">Role</label>
            <select className="input" value={jobTitle} onChange={e => setJobTitle(e.target.value)}>
              <option value="">— Select role —</option>
              {jobTitles.filter(j => j.isActive).map(j => <option key={j.id} value={j.name}>{j.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="field-label">Location</label>
            <select className="input" value={locationId} onChange={e => setLocationId(e.target.value)}>
              <option value="">— Select location —</option>
              {locations.filter(l => l.isActive).map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="field-label">Status</label>
            <select className="input" value={isActive ? 'active' : 'inactive'} onChange={e => setIsActive(e.target.value === 'active')}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={() => {
              if (fullName.trim() && email.trim() && department) {
                onSave({
                  fullName: fullName.trim(),
                  email: email.trim(),
                  phone,
                  employmentType,
                  basicSalaryUgx: baseSalary,
                  startDate,
                  department,
                  isActive,
                  ...(gender ? { gender } : {}),
                  ...(jobTitle ? { jobTitle } : {}),
                  ...(locationId ? { locationId } : {}),
                });
              }
            }}
            disabled={!fullName.trim() || !email.trim() || !department || isSaving}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function NameOnlyModal({
  title, fieldLabel, placeholder, initial, onClose, onSave, isSaving,
}: {
  title: string;
  fieldLabel: string;
  placeholder: string;
  initial?: { id?: string; name?: string };
  onClose: () => void;
  onSave: (data: { name: string }) => void;
  isSaving: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <h2 className="modal-title">{initial?.id ? `Edit ${title}` : `New ${title}`}</h2>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>&times;</button>
        </div>
        <div className="modal-body">
          <div className="field">
            <label className="field-label">{fieldLabel} *</label>
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder={placeholder} autoFocus />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => { if (name.trim()) onSave({ name: name.trim() }); }} disabled={!name.trim() || isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function LocationModal({
  initial, onClose, onSave, isSaving,
}: {
  initial?: Partial<Location>;
  onClose: () => void;
  onSave: (data: object) => void;
  isSaving: boolean;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [district, setDistrict] = useState(initial?.district ?? '');
  const [phone, setPhone] = useState(initial?.phone ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2 className="modal-title">{initial?.id ? 'Edit Location' : 'New Location'}</h2>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>&times;</button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label className="field-label">Location Name *</label>
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Kampala Main Branch" autoFocus />
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label className="field-label">Address</label>
            <input className="input" value={address} onChange={e => setAddress(e.target.value)} placeholder="e.g. Plot 12, Kampala Road" />
          </div>
          <div className="field">
            <label className="field-label">District</label>
            <input className="input" value={district} onChange={e => setDistrict(e.target.value)} placeholder="e.g. Kampala" />
          </div>
          <div className="field">
            <label className="field-label">Phone</label>
            <input className="input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+256 7XX XXX XXX" />
          </div>
          {initial?.id && (
            <div className="field">
              <label className="field-label">Status</label>
              <select className="input" value={isActive ? 'active' : 'inactive'} onChange={e => setIsActive(e.target.value === 'active')}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={() => {
              if (name.trim()) onSave({
                name: name.trim(),
                ...(address ? { address } : {}),
                ...(district ? { district } : {}),
                ...(phone ? { phone } : {}),
                ...(initial?.id ? { isActive } : {}),
              });
            }}
            disabled={!name.trim() || isSaving}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function HrPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<'employees' | 'leave' | 'departments' | 'roles' | 'locations'>('employees');
  const [empModal, setEmpModal] = useState<{ open: boolean; editing?: Employee }>({ open: false });
  const [deptModal, setDeptModal] = useState<{ open: boolean; editing?: Department }>({ open: false });
  const [roleModal, setRoleModal] = useState<{ open: boolean; editing?: JobTitle }>({ open: false });
  const [locModal, setLocModal] = useState<{ open: boolean; editing?: Location }>({ open: false });
  const [activeOnly, setActiveOnly] = useState(true);

  const { data: allEmployees = [], isLoading: empLoading } = useQuery<Employee[]>({
    queryKey: ['employees'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/hr/employees?activeOnly=false`, { headers: authHeaders() });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
      return j.data ?? [];
    },
  });
  const employees = activeOnly
    ? allEmployees.filter(e => e.isActive)
    : allEmployees.filter(e => !e.isActive);

  const { data: leaveRequests = [], isLoading: leaveLoading } = useQuery<LeaveRequest[]>({
    queryKey: ['leave-requests'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/hr/leave-requests`, { headers: authHeaders() });
      const j = await res.json();
      return j.data ?? [];
    },
  });

  const { data: departments = [], isLoading: deptLoading } = useQuery<Department[]>({
    queryKey: ['departments'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/hr/departments`, { headers: authHeaders() });
      const j = await res.json();
      return j.data ?? [];
    },
  });

  const { data: jobTitles = [], isLoading: rolesLoading } = useQuery<JobTitle[]>({
    queryKey: ['job-titles'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/hr/job-titles`, { headers: authHeaders() });
      const j = await res.json();
      return j.data ?? [];
    },
  });

  const { data: locations = [], isLoading: locLoading } = useQuery<Location[]>({
    queryKey: ['locations'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/locations`, { headers: authHeaders() });
      const j = await res.json();
      return j.data ?? [];
    },
  });

  const saveEmployee = useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: object }) => {
      const res = await fetch(
        id ? `${API_BASE}/v1/hr/employees/${id}` : `${API_BASE}/v1/hr/employees`,
        { method: id ? 'PATCH' : 'POST', headers: authHeaders(), body: JSON.stringify(data) },
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Save failed');
      return j.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['employees'] }); setEmpModal({ open: false }); },
  });

  const approveLeave = useMutation({
    mutationFn: async ({ id, approved }: { id: string; approved: boolean }) => {
      const res = await fetch(`${API_BASE}/v1/hr/leave-requests/${id}/approve`, {
        method: 'PATCH', headers: authHeaders(),
        body: JSON.stringify({ approved }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leave-requests'] }),
  });

  const saveDepartment = useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: { name: string } }) => {
      const res = await fetch(
        id ? `${API_BASE}/v1/hr/departments/${id}` : `${API_BASE}/v1/hr/departments`,
        { method: id ? 'PATCH' : 'POST', headers: authHeaders(), body: JSON.stringify(data) },
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Save failed');
      return j.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['departments'] }); setDeptModal({ open: false }); },
  });

  const saveRole = useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: { name: string } }) => {
      const res = await fetch(
        id ? `${API_BASE}/v1/hr/job-titles/${id}` : `${API_BASE}/v1/hr/job-titles`,
        { method: id ? 'PATCH' : 'POST', headers: authHeaders(), body: JSON.stringify(data) },
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Save failed');
      return j.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['job-titles'] }); setRoleModal({ open: false }); },
  });

  const saveLocation = useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: object }) => {
      const res = await fetch(
        id ? `${API_BASE}/v1/locations/${id}` : `${API_BASE}/v1/locations`,
        { method: id ? 'PATCH' : 'POST', headers: authHeaders(), body: JSON.stringify(data) },
      );
      const j = await res.json();
      if (!res.ok) throw new Error(j.error?.message ?? 'Save failed');
      return j.data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['locations'] }); setLocModal({ open: false }); },
  });

  const pendingLeave = leaveRequests.filter(l => l.status === 'PENDING').length;

  const locationName = (id?: string) => locations.find(l => l.id === id)?.name ?? '—';

  const TABS = [
    { key: 'employees', label: 'Employees' },
    { key: 'leave', label: 'Leave Requests' },
    { key: 'departments', label: 'Departments' },
    { key: 'roles', label: 'Roles' },
    { key: 'locations', label: 'Locations' },
  ] as const;

  return (
    <div>
      <div className="page-header">
        <div>
          <p style={{ color: 'var(--text-3)', fontSize: 13 }}>
            {allEmployees.filter(e => e.isActive).length} active employees · {pendingLeave} leave pending
          </p>
        </div>
        {activeTab === 'employees' && (
          <button className="btn btn-primary" onClick={() => setEmpModal({ open: true })}>+ Employee</button>
        )}
        {activeTab === 'departments' && (
          <button className="btn btn-primary" onClick={() => setDeptModal({ open: true })}>+ Department</button>
        )}
        {activeTab === 'roles' && (
          <button className="btn btn-primary" onClick={() => setRoleModal({ open: true })}>+ Role</button>
        )}
        {activeTab === 'locations' && (
          <button className="btn btn-primary" onClick={() => setLocModal({ open: true })}>+ Location</button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: 'var(--surface2)', borderRadius: 10, padding: 4, width: 'fit-content', marginBottom: 20 }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '6px 18px', borderRadius: 8, fontSize: 13, fontWeight: activeTab === tab.key ? 600 : 400,
              background: activeTab === tab.key ? 'var(--surface)' : 'transparent',
              color: activeTab === tab.key ? 'var(--text)' : 'var(--text-3)',
              border: 'none', cursor: 'pointer',
              boxShadow: activeTab === tab.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.12s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'employees' && (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-2)', marginBottom: 16, width: 'fit-content' }}>
            <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} />
            Active employees only
          </label>
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            {empLoading ? (
              <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} />)}
              </div>
            ) : employees.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No employees found.</div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Role</th>
                      <th>Department</th>
                      <th>Location</th>
                      <th>Gender</th>
                      <th style={{ textAlign: 'center' }}>Type</th>
                      <th style={{ textAlign: 'right' }}>Salary</th>
                      <th style={{ textAlign: 'center' }}>Status</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map(emp => (
                      <tr key={emp.id}>
                        <td>
                          <p style={{ fontWeight: 500, color: 'var(--text)' }}>{emp.fullName}</p>
                          <p style={{ fontSize: 11, color: 'var(--text-3)' }}>{emp.email}</p>
                        </td>
                        <td style={{ color: 'var(--text-2)' }}>{emp.jobTitle ?? '—'}</td>
                        <td style={{ color: 'var(--text-2)' }}>{emp.department ?? '—'}</td>
                        <td style={{ color: 'var(--text-2)' }}>{locationName(emp.locationId)}</td>
                        <td style={{ color: 'var(--text-2)', textTransform: 'capitalize' }}>{emp.gender ?? '—'}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={`badge ${empTypeBadge(emp.employmentType)}`}>
                            {emp.employmentType === 'fulltime' ? 'Full Time' : emp.employmentType === 'parttime' ? 'Part Time' : emp.employmentType === 'contract' ? 'Contract' : 'Casual'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right', color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
                          {emp.salaryStructures?.[0]?.basicSalaryUgx ? fmtUGX(emp.salaryStructures[0].basicSalaryUgx) : '—'}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={`badge ${emp.isActive ? 'badge-green' : 'badge-gray'}`}>{emp.isActive ? 'Active' : 'Inactive'}</span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => setEmpModal({ open: true, editing: emp })}>Edit</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'leave' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {leaveLoading ? (
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} />)}
            </div>
          ) : leaveRequests.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No leave requests.</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Type</th>
                    <th>Period</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {leaveRequests.map(lr => (
                    <tr key={lr.id}>
                      <td style={{ fontWeight: 500, color: 'var(--text)' }}>{lr.employee?.fullName ?? '—'}</td>
                      <td style={{ color: 'var(--text-2)' }}>{lr.leaveType.replace('_', ' ')}</td>
                      <td style={{ color: 'var(--text-2)' }}>{fmtDate(lr.startDate)} – {fmtDate(lr.endDate)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${leaveStatusBadge(lr.status)}`}>{lr.status}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {lr.status === 'PENDING' && (
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--green)' }} onClick={() => approveLeave.mutate({ id: lr.id, approved: true })}>Approve</button>
                            <button className="btn btn-danger btn-sm" onClick={() => approveLeave.mutate({ id: lr.id, approved: false })}>Reject</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'departments' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {deptLoading ? (
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={48} />)}
            </div>
          ) : departments.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
              No departments yet. Click <strong>+ Department</strong> to create one.
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Department</th><th style={{ textAlign: 'center' }}>Status</th><th /></tr></thead>
                <tbody>
                  {departments.map(d => (
                    <tr key={d.id}>
                      <td style={{ fontWeight: 500, color: 'var(--text)' }}>{d.name}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${d.isActive ? 'badge-green' : 'badge-gray'}`}>{d.isActive ? 'Active' : 'Inactive'}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setDeptModal({ open: true, editing: d })}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'roles' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {rolesLoading ? (
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={48} />)}
            </div>
          ) : jobTitles.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
              No roles yet. Click <strong>+ Role</strong> to create one.
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Role</th><th style={{ textAlign: 'center' }}>Status</th><th /></tr></thead>
                <tbody>
                  {jobTitles.map(j => (
                    <tr key={j.id}>
                      <td style={{ fontWeight: 500, color: 'var(--text)' }}>{j.name}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${j.isActive ? 'badge-green' : 'badge-gray'}`}>{j.isActive ? 'Active' : 'Inactive'}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setRoleModal({ open: true, editing: j })}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'locations' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {locLoading ? (
            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} h={48} />)}
            </div>
          ) : locations.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
              No locations yet. Click <strong>+ Location</strong> to create one.
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Location</th>
                    <th>Address</th>
                    <th>District</th>
                    <th>Phone</th>
                    <th style={{ textAlign: 'center' }}>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {locations.map(l => (
                    <tr key={l.id}>
                      <td style={{ fontWeight: 500, color: 'var(--text)' }}>{l.name}</td>
                      <td style={{ color: 'var(--text-2)' }}>{l.address ?? '—'}</td>
                      <td style={{ color: 'var(--text-2)' }}>{l.district ?? '—'}</td>
                      <td style={{ color: 'var(--text-2)' }}>{l.phone ?? '—'}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge ${l.isActive ? 'badge-green' : 'badge-gray'}`}>{l.isActive ? 'Active' : 'Inactive'}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setLocModal({ open: true, editing: l })}>Edit</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {empModal.open && (
        <EmployeeModal
          initial={empModal.editing}
          departments={departments}
          jobTitles={jobTitles}
          locations={locations}
          onClose={() => setEmpModal({ open: false })}
          onSave={data => saveEmployee.mutate({ id: empModal.editing?.id, data })}
          isSaving={saveEmployee.isPending}
        />
      )}

      {deptModal.open && (
        <NameOnlyModal
          title="Department" fieldLabel="Department Name" placeholder="e.g. Kitchen"
          initial={deptModal.editing}
          onClose={() => setDeptModal({ open: false })}
          onSave={data => saveDepartment.mutate({ id: deptModal.editing?.id, data })}
          isSaving={saveDepartment.isPending}
        />
      )}

      {roleModal.open && (
        <NameOnlyModal
          title="Role" fieldLabel="Role Name" placeholder="e.g. Head Chef"
          initial={roleModal.editing}
          onClose={() => setRoleModal({ open: false })}
          onSave={data => saveRole.mutate({ id: roleModal.editing?.id, data })}
          isSaving={saveRole.isPending}
        />
      )}

      {locModal.open && (
        <LocationModal
          initial={locModal.editing}
          onClose={() => setLocModal({ open: false })}
          onSave={data => saveLocation.mutate({ id: locModal.editing?.id, data })}
          isSaving={saveLocation.isPending}
        />
      )}
    </div>
  );
}
